import * as THREE from 'three'
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js'
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js'
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js'
import type { MeasurementState } from '@/viewer/types'

/**
 * Measurement overlay: committed measurement lines + endpoint dots, the
 * in-progress preview line and the snap marker under the cursor.
 *
 * Everything renders on top (depthTest off) and ignores the global clipping
 * planes, so measurements stay readable while a section is active.
 */

const ON_TOP = 1000

export type SnapKind = 'vertex' | 'edge' | 'face'

const SNAP_COLORS: Record<SnapKind, number> = {
  vertex: 0xff9f1c,
  edge: 0x22d3ee,
  face: 0xffffff,
}

function dotMaterial(color: number, size: number, ring: boolean): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    transparent: true,
    depthTest: false,
    depthWrite: false,
    // clipping stays false (ShaderMaterial default): dots are never clipped
    uniforms: {
      uColor: { value: new THREE.Color(color) },
      uSize: { value: size },
      uRing: { value: ring ? 1 : 0 },
      uPixelRatio: { value: 1 },
    },
    vertexShader: /* glsl */ `
      uniform float uSize;
      uniform float uPixelRatio;
      void main() {
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        gl_PointSize = uSize * uPixelRatio;
      }
    `,
    fragmentShader: /* glsl */ `
      uniform vec3 uColor;
      uniform float uRing;
      void main() {
        vec2 c = gl_PointCoord - 0.5;
        float d = length(c);
        if (d > 0.5) discard;
        // dark outline so dots read on both light and dark geometry
        vec3 col = d > 0.36 ? vec3(0.04) : uColor;
        if (uRing > 0.5 && d < 0.2) discard;
        gl_FragColor = vec4(col, 1.0);
      }
    `,
  })
}

function lineMaterial(color: number, width: number, dashed: boolean): LineMaterial {
  const mat = new LineMaterial({
    color,
    linewidth: width,
    dashed,
    dashSize: 0.15,
    gapSize: 0.1,
    depthTest: false,
    depthWrite: false,
    transparent: true,
  })
  // LineMaterial opts into clipping; measurements must not be clipped away
  mat.clipping = false
  return mat
}

interface MeasureVisual {
  line: LineSegments2
  dots: THREE.Points
}

export class MeasureOverlay {
  readonly group = new THREE.Group()
  private readonly lineMat = lineMaterial(0xffb020, 2.5, false)
  private readonly previewMat = lineMaterial(0xffb020, 1.5, false)
  private readonly dotMat = dotMaterial(0xffb020, 9, false)
  private readonly snapMats: Record<SnapKind, THREE.ShaderMaterial> = {
    vertex: dotMaterial(SNAP_COLORS.vertex, 14, true),
    edge: dotMaterial(SNAP_COLORS.edge, 12, true),
    face: dotMaterial(SNAP_COLORS.face, 9, false),
  }
  private readonly visuals = new Map<string, MeasureVisual>()
  private readonly snap: THREE.Points
  private readonly preview: LineSegments2
  private readonly startDot: THREE.Points

  constructor() {
    this.group.name = 'bimdrop-measure-overlay'
    this.group.renderOrder = ON_TOP

    const snapGeom = new THREE.BufferGeometry()
    snapGeom.setAttribute('position', new THREE.Float32BufferAttribute([0, 0, 0], 3))
    this.snap = new THREE.Points(snapGeom, this.snapMats.face)
    this.snap.renderOrder = ON_TOP + 2
    this.snap.frustumCulled = false
    this.snap.visible = false

    const startGeom = new THREE.BufferGeometry()
    startGeom.setAttribute('position', new THREE.Float32BufferAttribute([0, 0, 0], 3))
    this.startDot = new THREE.Points(startGeom, this.dotMat)
    this.startDot.renderOrder = ON_TOP + 1
    this.startDot.frustumCulled = false
    this.startDot.visible = false

    const previewGeom = new LineSegmentsGeometry()
    previewGeom.setPositions([0, 0, 0, 0, 0, 0])
    this.preview = new LineSegments2(previewGeom, this.previewMat)
    this.preview.renderOrder = ON_TOP
    this.preview.frustumCulled = false
    this.preview.visible = false

    this.group.add(this.snap, this.startDot, this.preview)
  }

  /** Keep line widths and point sizes in CSS pixels. Call before each render. */
  setViewport(width: number, height: number, pixelRatio: number) {
    for (const mat of [this.lineMat, this.previewMat]) mat.resolution.set(width, height)
    for (const mat of [this.dotMat, ...Object.values(this.snapMats)]) mat.uniforms.uPixelRatio.value = pixelRatio
  }

  add(m: MeasurementState) {
    this.remove(m.id)
    const geom = new LineSegmentsGeometry()
    geom.setPositions([...m.a, ...m.b])
    const line = new LineSegments2(geom, this.lineMat)
    line.renderOrder = ON_TOP
    line.frustumCulled = false
    const dotGeom = new THREE.BufferGeometry()
    dotGeom.setAttribute('position', new THREE.Float32BufferAttribute([...m.a, ...m.b], 3))
    const dots = new THREE.Points(dotGeom, this.dotMat)
    dots.renderOrder = ON_TOP + 1
    dots.frustumCulled = false
    this.group.add(line, dots)
    this.visuals.set(m.id, { line, dots })
  }

  remove(id: string) {
    const v = this.visuals.get(id)
    if (!v) return
    v.line.removeFromParent()
    v.dots.removeFromParent()
    v.line.geometry.dispose()
    v.dots.geometry.dispose()
    this.visuals.delete(id)
  }

  clear() {
    for (const id of [...this.visuals.keys()]) this.remove(id)
  }

  showSnap(point: THREE.Vector3 | null, kind: SnapKind = 'face') {
    if (!point) {
      this.snap.visible = false
      return
    }
    this.snap.material = this.snapMats[kind]
    const attr = this.snap.geometry.getAttribute('position') as THREE.BufferAttribute
    attr.setXYZ(0, point.x, point.y, point.z)
    attr.needsUpdate = true
    this.snap.visible = true
  }

  /** Start dot + preview line from `a` to `b` (b null hides the line). */
  showPreview(a: THREE.Vector3 | null, b: THREE.Vector3 | null) {
    if (!a) {
      this.startDot.visible = false
      this.preview.visible = false
      return
    }
    const attr = this.startDot.geometry.getAttribute('position') as THREE.BufferAttribute
    attr.setXYZ(0, a.x, a.y, a.z)
    attr.needsUpdate = true
    this.startDot.visible = true
    if (b) {
      this.preview.geometry.setPositions([a.x, a.y, a.z, b.x, b.y, b.z])
      this.preview.visible = true
    } else {
      this.preview.visible = false
    }
  }

  dispose() {
    this.clear()
    this.group.removeFromParent()
    this.snap.geometry.dispose()
    this.startDot.geometry.dispose()
    this.preview.geometry.dispose()
    this.lineMat.dispose()
    this.previewMat.dispose()
    this.dotMat.dispose()
    for (const m of Object.values(this.snapMats)) m.dispose()
  }
}
